import clsx from 'clsx';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import * as React from 'react';
import { Link } from 'react-aria-components';

import { Icon } from '@gitbook/spine-icons';
import { useSafeContext } from '@gitbook/util-react-hooks';

import { Button } from '../Button';
import { confirm } from '../Dialog';
import {
    Disclosure,
    DisclosurePanel,
    DisclosureStateContext,
    DisclosureTrigger,
} from '../Disclosure';
import type { Href, RouterOptions } from '../Router';
import { type IconDefinition, renderIcon } from '../util';

/** `current` is the step to work on, once started; `next` is the step to work on, before. */
export type StepProgressCardStepState = 'done' | 'current' | 'next' | 'pending' | 'skipped';

export interface StepProgressCardStep {
    /** Stable identifier used as the rendered list key. */
    id: string;
    /** Human-readable step title. */
    title: string;
    /** Completion state of the step. */
    state: StepProgressCardStepState;
    /** Navigation target opened when the step or its progress segment is pressed. */
    href: Href;
    /** Optional router-specific navigation options forwarded to links. */
    routerOptions?: RouterOptions;
    /** Called when the step is pressed, before it navigates to `href`. */
    onPress?: () => void;
}

export interface StepProgressCardProps extends Omit<
    React.ComponentPropsWithoutRef<'section'>,
    'children'
> {
    /** Ordered steps used to derive progress, the current step, and navigation targets. */
    steps: readonly StepProgressCardStep[];
    /** Composed header, progress, steps, and footer content. */
    children: React.ReactNode;
    /** Whether the step list is expanded initially. Defaults to `false`. */
    defaultExpanded?: boolean;
    /** Controlled expanded state. */
    isExpanded?: boolean;
    /** Called whenever the expanded state changes. */
    onExpandedChange?: (isExpanded: boolean) => void;
}

/**
 * Progress segment look per step state, with its hover: a step left aside reads as an empty
 * dotted track. `neutral-ui-hover` is the same shade as `neutral-ui`, hence `standout`.
 */
const STEP_SEGMENT_CLASSES: Record<StepProgressCardStepState, string> = {
    done: 'bg-primary-solid group-hover/progress-step:bg-primary-solid-hover',
    current: 'bg-primary-solid group-hover/progress-step:bg-primary-solid-hover',
    next: 'bg-neutral-ui group-hover/progress-step:bg-neutral-ui-standout',
    pending: 'bg-neutral-ui group-hover/progress-step:bg-neutral-ui-standout',
    skipped:
        'border border-dotted border-neutral-subtle bg-base group-hover/progress-step:border-neutral-interactive',
};

type StepProgressCardContextValue = {
    steps: readonly StepProgressCardStep[];
    progressCount: number;
    nextStep: StepProgressCardStep | undefined;
    nextStepIndex: number;
    /** The step whose progress segment is hovered, named by the footer in place of the next one. */
    hoveredStep: StepProgressCardStep | undefined;
    setHoveredStepId: (id: string | null) => void;
};

const StepProgressCardContext = React.createContext<StepProgressCardContextValue | null>(null);

/**
 * A compact, composable sidebar card for a short sequence of navigable tasks. It derives progress
 * from step state while consumers compose its header, progress track, expanded list, and footer.
 *
 * Unlike {@link SidebarAlert}, it models persistent multi-step progress and navigation. Unlike
 * {@link Stepper}, it is sized and structured specifically for a collapsible sidebar surface.
 */
export function StepProgressCard({
    ref,
    ...props
}: StepProgressCardProps & { ref?: React.ForwardedRef<HTMLElement> }) {
    const {
        children,
        className,
        defaultExpanded = false,
        isExpanded,
        onExpandedChange,
        steps,
        ...rest
    } = props;

    const nextStepIndex = steps.findIndex(isNextStep);
    const [hoveredStepId, setHoveredStepId] = React.useState<string | null>(null);

    const context = React.useMemo<StepProgressCardContextValue>(
        () => ({
            steps,
            // A skipped step is behind the user as much as a done one, and the step in progress is
            // the one they are on.
            progressCount: steps.filter(
                (step) =>
                    step.state === 'done' || step.state === 'skipped' || step.state === 'current'
            ).length,
            nextStep: steps[nextStepIndex],
            nextStepIndex,
            hoveredStep: steps.find((step) => step.id === hoveredStepId),
            setHoveredStepId,
        }),
        [hoveredStepId, nextStepIndex, steps]
    );

    return (
        <StepProgressCardContext.Provider value={context}>
            <Disclosure
                defaultExpanded={defaultExpanded}
                isExpanded={isExpanded}
                onExpandedChange={onExpandedChange}
            >
                <section
                    ref={ref}
                    {...rest}
                    className={clsx(
                        'flex w-full flex-col rounded-lg border border-control bg-base p-2.5 shadow-subtle',
                        className
                    )}
                >
                    {children}
                </section>
            </Disclosure>
        </StepProgressCardContext.Provider>
    );
}

export interface StepProgressCardHeaderProps extends Omit<
    React.ComponentPropsWithoutRef<'div'>,
    'children' | 'title'
> {
    /** Icon identifying the progress sequence. */
    icon: IconDefinition;
    /** Card heading. */
    title: React.ReactNode;
    /** Trailing action, normally a {@link StepProgressCardDismissButton}. */
    children?: React.ReactNode;
}

/** Header row for a {@link StepProgressCard}, including its derived counter and toggle. */
export function StepProgressCardHeader({
    ref,
    ...props
}: StepProgressCardHeaderProps & { ref?: React.ForwardedRef<HTMLDivElement> }) {
    const { children, className, icon, title, ...rest } = props;
    const { progressCount, steps } = useStepProgressCardContext();
    const disclosure = useSafeContext(
        DisclosureStateContext,
        'StepProgressCardHeader must be used within StepProgressCard'
    );

    return (
        <div
            ref={ref}
            {...rest}
            className={clsx('ml-0.5 flex min-w-0 items-center gap-2', className)}
        >
            {renderIcon(icon, { size: 18, className: 'shrink-0 text-base' })}
            <h3 className="min-w-0 flex-1 truncate text-ui-heading-small">{title}</h3>
            <span className="shrink-0 text-ui-base text-muted tabular-nums">
                {progressCount} of {steps.length}
            </span>
            <DisclosureTrigger>
                <Button
                    kind="blank"
                    size="xsmall"
                    iconOnly
                    label={disclosure.isExpanded ? 'Collapse' : 'Expand'}
                    leadingIcon={
                        <Icon.ChevronDown className="rotate-180 transition-transform group-aria-expanded/button:rotate-0 motion-reduce:transition-none" />
                    }
                />
            </DisclosureTrigger>
            {children}
        </div>
    );
}

export interface StepProgressCardProgressProps extends Omit<
    React.ComponentPropsWithoutRef<'nav'>,
    'children'
> {
    /** Accessible label describing the progress navigation. */
    label?: string;
}

/** Compact progress navigation for a {@link StepProgressCard}. */
export function StepProgressCardProgress({
    ref,
    ...props
}: StepProgressCardProgressProps & { ref?: React.ForwardedRef<HTMLElement> }) {
    const { className, label = 'Progress steps', ...rest } = props;
    const { steps, setHoveredStepId } = useStepProgressCardContext();

    return (
        <nav ref={ref} {...rest} aria-label={label} className={clsx('mt-3', className)}>
            {/* No gap between the links, only padding inside them: moving from one segment to the
                next never leaves them all, which would flash the footer back to the next step. */}
            <ol className="-mx-0.5 flex">
                {steps.map((step, index) => (
                    <li key={step.id} className="min-w-0 flex-1">
                        <Link
                            href={step.href}
                            routerOptions={step.routerOptions}
                            onPress={step.onPress}
                            aria-current={isNextStep(step) ? 'step' : undefined}
                            aria-label={`${index + 1}. ${step.title}`}
                            onHoverChange={(isHovered) =>
                                setHoveredStepId(isHovered ? step.id : null)
                            }
                            className="group/progress-step -my-1 flex rounded-xs px-0.5 py-1 outline-none data-focus-visible:focus-ring"
                        >
                            <span
                                className={clsx(
                                    'block h-1 w-full rounded-full transition-colors',
                                    STEP_SEGMENT_CLASSES[step.state]
                                )}
                            />
                        </Link>
                    </li>
                ))}
            </ol>
        </nav>
    );
}

export interface StepProgressCardStepsProps extends Omit<
    React.ComponentPropsWithoutRef<'div'>,
    'children'
> {
    /** Label shown at the end of completed rows. */
    doneLabel?: string;
    /** Label shown at the end of the row to work on, once started. */
    currentLabel?: string;
    /** Label shown at the end of the row to work on, before it is started. */
    nextLabel?: string;
    /** Label shown at the end of skipped rows. */
    skippedLabel?: string;
}

/** Animated expanded list of linked steps for a {@link StepProgressCard}. */
export function StepProgressCardSteps({
    ref,
    ...props
}: StepProgressCardStepsProps & { ref?: React.ForwardedRef<HTMLDivElement> }) {
    const {
        className,
        doneLabel = 'Done',
        currentLabel = 'In progress',
        nextLabel = 'Next',
        skippedLabel = 'Skipped',
        ...rest
    } = props;
    const { steps } = useStepProgressCardContext();
    const disclosure = useSafeContext(
        DisclosureStateContext,
        'StepProgressCardSteps must be used within StepProgressCard'
    );

    return (
        <DisclosurePanel
            ref={ref}
            {...rest}
            inert={disclosure.isExpanded ? undefined : true}
            className={className}
        >
            <ol className="flex flex-col gap-0.5 pt-3">
                {steps.map((step) => (
                    <li key={step.id}>
                        <Link
                            href={step.href}
                            routerOptions={step.routerOptions}
                            onPress={step.onPress}
                            aria-current={isNextStep(step) ? 'step' : undefined}
                            className={clsx(
                                'flex min-h-8 items-center gap-2 rounded-sm px-1.5 py-1 text-ui-base transition-colors outline-none',
                                'hover:bg-neutral-ui-hover data-focus-visible:focus-ring-inset',
                                isNextStep(step) && 'bg-neutral-surface-subtle'
                            )}
                        >
                            <StepProgressCardStepIcon state={step.state} />
                            <span
                                className={clsx(
                                    'min-w-0 flex-1 truncate',
                                    step.state === 'done' && 'text-muted line-through'
                                )}
                            >
                                {step.title}
                            </span>
                            {step.state === 'done' ? (
                                <span className="shrink-0 text-muted">{doneLabel}</span>
                            ) : step.state === 'current' ? (
                                <span className="shrink-0 text-muted">{currentLabel}</span>
                            ) : step.state === 'next' ? (
                                <span className="shrink-0 text-muted">{nextLabel}</span>
                            ) : step.state === 'skipped' ? (
                                <span className="shrink-0 text-muted">{skippedLabel}</span>
                            ) : null}
                        </Link>
                    </li>
                ))}
            </ol>
        </DisclosurePanel>
    );
}

export interface StepProgressCardFooterProps extends Omit<
    React.ComponentPropsWithoutRef<'div'>,
    'children'
> {}

/** Footer for a {@link StepProgressCard}, derived from the step marked `current` or `next`. */
export function StepProgressCardFooter({
    ref,
    ...props
}: StepProgressCardFooterProps & { ref?: React.ForwardedRef<HTMLDivElement> }) {
    const { className, ...rest } = props;
    const { nextStep, nextStepIndex, hoveredStep } = useStepProgressCardContext();
    const prefersReducedMotion = useReducedMotion();

    if (!nextStep) {
        return null;
    }

    const label = hoveredStep
        ? hoveredStep.title
        : nextStep.state === 'current'
          ? nextStep.title
          : `Next: ${nextStep.title}`;

    return (
        <div
            ref={ref}
            {...rest}
            className={clsx('mt-3 flex min-w-0 items-center justify-between gap-2', className)}
        >
            <p className="relative min-w-0 flex-1 overflow-hidden text-ui-base text-muted">
                <AnimatePresence initial={false} mode="popLayout">
                    <motion.span
                        key={label}
                        className="block truncate"
                        initial={{ opacity: 0, y: 4 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -4 }}
                        transition={{ duration: prefersReducedMotion ? 0 : 0.15, ease: 'easeOut' }}
                    >
                        {label}
                    </motion.span>
                </AnimatePresence>
            </p>
            <Button
                href={nextStep.href}
                routerOptions={nextStep.routerOptions}
                onPress={nextStep.onPress}
                kind="secondary"
                size="small"
                label={nextStepIndex === 0 && nextStep.state === 'next' ? 'Start' : 'Resume'}
            />
        </div>
    );
}

export interface StepProgressCardDismissButtonProps {
    /** Accessible label for the icon-only dismiss button. */
    label?: string;
    /** Confirmation dialog heading. */
    dialogTitle: React.ReactNode;
    /** Optional confirmation dialog description. */
    dialogDescription?: React.ReactNode;
    /** Called only after the user confirms dismissal. */
    onDismiss: () => void | Promise<void>;
}

/** Confirmation-backed dismiss action intended for {@link StepProgressCardHeader}. */
export function StepProgressCardDismissButton(props: StepProgressCardDismissButtonProps) {
    const { dialogDescription, dialogTitle, label = 'Dismiss progress', onDismiss } = props;

    return (
        <Button
            kind="blank"
            size="xsmall"
            iconOnly
            label={label}
            leadingIcon={Icon.X}
            onPress={() => {
                void confirm({
                    cancelLabel: 'Cancel',
                    confirmLabel: 'Dismiss setup',
                    description: dialogDescription,
                    title: dialogTitle,
                    onConfirm: async () => {
                        await onDismiss();
                    },
                });
            }}
        />
    );
}

/** Reads the derived step data shared by the composed progress-card parts. */
function useStepProgressCardContext() {
    return useSafeContext(
        StepProgressCardContext,
        'StepProgressCard parts must be used within StepProgressCard'
    );
}

/** Whether a step is the one to work on, started or not. */
function isNextStep(step: StepProgressCardStep): boolean {
    return step.state === 'current' || step.state === 'next';
}

/** State indicator used by rows in {@link StepProgressCardSteps}. */
function StepProgressCardStepIcon(props: { state: StepProgressCardStepState }) {
    if (props.state === 'done') {
        return (
            <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-success-solid text-inverted">
                <Icon.Check className="size-3.5" />
            </span>
        );
    }

    if (props.state === 'current' || props.state === 'next') {
        return (
            <span className="flex size-5 shrink-0 items-center justify-center rounded-full border-2 border-neutral-high">
                <span className="size-1.5 rounded-full bg-neutral-solid" />
            </span>
        );
    }

    if (props.state === 'skipped') {
        return (
            <span className="size-5 shrink-0 rounded-full border-2 border-dashed border-neutral-subtle" />
        );
    }

    return <span className="size-5 shrink-0 rounded-full border-2 border-neutral-subtle" />;
}
